/**
 * Tutorial Workflow Viewer - React Flow visualization for tutorial workflow steps
 * Similar to planner-workflow-viewer but for tutorial workflows
 */

import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge as RFEdge,
  Handle,
  MarkerType,
  type Node as RFNode,
  type NodeProps,
  type NodeTypes,
  Position,
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import { Cog, CheckCircle2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "#app/utils/misc";
import { type PlannerWorkflow } from "#app/utils/types.ts";

const LAYER_Y_GAP = 180;
const NODE_X_GAP = 340;

// `Node`'s data parameter is constrained to `Record<string, unknown>` in
// @xyflow/react v12, hence the index signature.
type TutorialWorkflowNodeData = {
  name: string;
  description: string;
  color: string;
  iconUrls?: (string | null)[];
  isCompleted?: boolean;
  isCurrent?: boolean;
  [key: string]: unknown;
};

type CircleNodeData = {
  label: string;
  variant: "start" | "end";
  [key: string]: unknown;
};

type TutorialStepNode = RFNode<TutorialWorkflowNodeData, "tutorialWorkflow">;
type CircleNodeType = RFNode<CircleNodeData, "circle">;

/** Every node the graph builder can emit. */
type TutorialGraphNode = TutorialStepNode | CircleNodeType;

function buildTutorialWorkflowGraph(
  workflow: PlannerWorkflow | null | undefined,
  completedStepIds: string[] = [],
  currentStepId?: string,
): { nodes: TutorialGraphNode[]; edges: RFEdge[] } {
  if (!workflow?.steps || !Array.isArray(workflow.steps))
    return { nodes: [], edges: [] };

  const validSteps = workflow.steps.filter(
    (step) =>
      step &&
      typeof step === "object" &&
      step.id &&
      step.name &&
      typeof step.id === "string" &&
      typeof step.name === "string",
  );

  if (validSteps.length === 0) return { nodes: [], edges: [] };

  const stepById = new Map(validSteps.map((s) => [s.id, s]));
  const edges: RFEdge[] = [];

  // Build edges
  for (const step of validSteps) {
    // depends_on edges
    for (const dep of step.depends_on || []) {
      if (stepById.has(dep)) {
        edges.push({
          id: `depends_${dep}->${step.id}`,
          source: dep,
          target: step.id,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 20,
            height: 20,
          },
        });
      }
    }
    // next_steps edges
    if (step.next_steps) {
      for (const ns of step.next_steps) {
        if (stepById.has(ns.step_id)) {
          edges.push({
            id: `next_${step.id}->${ns.step_id}`,
            source: step.id,
            target: ns.step_id,
            label: ns.condition?.name,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
            },
          });
        }
      }
    }
  }

  // Level assignment (top-down)
  const inDegree = new Map<string, number>();
  for (const step of validSteps) inDegree.set(step.id, 0);
  for (const e of edges) {
    inDegree.set(e.target, (inDegree.get(e.target) || 0) + 1);
  }

  const level = new Map<string, number>();
  const queue: string[] = [];

  const startAt = Array.isArray(workflow.start_at)
    ? workflow.start_at
    : typeof workflow.start_at === "string"
      ? [workflow.start_at]
      : [];
  const startSet = new Set(startAt);
  if (startSet.size > 0) {
    for (const id of startSet) {
      level.set(id, 0);
      queue.push(id);
    }
  } else {
    for (const [id, deg] of inDegree) {
      if (deg === 0) {
        level.set(id, 0);
        queue.push(id);
      }
    }
  }

  const out = new Map<string, string[]>();
  for (const e of edges) {
    const list = out.get(e.source) || [];
    list.push(e.target);
    out.set(e.source, list);
  }

  while (queue.length) {
    const u = queue.shift()!;
    const next = out.get(u) || [];
    for (const v of next) {
      const nextLevel = (level.get(u) || 0) + 1;
      if (!level.has(v) || nextLevel > (level.get(v) as number)) {
        level.set(v, nextLevel);
        queue.push(v);
      }
    }
  }

  for (const s of validSteps) {
    if (!level.has(s.id)) level.set(s.id, 0);
  }

  const byLevel = new Map<number, string[]>();
  for (const [id, lvl] of level) {
    const list = byLevel.get(lvl) || [];
    list.push(id);
    byLevel.set(lvl, list);
  }

  const nodes: TutorialGraphNode[] = [];
  const sortedLevels = Array.from(byLevel.keys()).sort((a, b) => a - b);
  for (const lvl of sortedLevels) {
    const ids = byLevel.get(lvl)!;
    ids.sort();
    const count = ids.length;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]!;
      const step = stepById.get(id)!;
      const x = (i - (count - 1) / 2) * NODE_X_GAP;
      const y = lvl * LAYER_Y_GAP;

      const isCompleted = completedStepIds.includes(id);
      const isCurrent = id === currentStepId;

      nodes.push({
        id,
        type: "tutorialWorkflow",
        position: { x, y },
        data: {
          name: step.name,
          description: step.description,
          color: "#E0F2FE",
          isCompleted,
          isCurrent,
        },
        draggable: true,
      });
    }
  }

  // Add Start and End nodes
  const entryIds =
    workflow.start_at && workflow.start_at.length > 0
      ? workflow.start_at
      : Array.from(inDegree.entries())
          .filter(([, deg]) => deg === 0)
          .map(([id]) => id);

  const outDegree = new Map<string, number>();
  for (const s of validSteps) outDegree.set(s.id, 0);
  for (const e of edges) {
    outDegree.set(e.source, (outDegree.get(e.source) || 0) + 1);
  }
  const terminalIds = Array.from(outDegree.entries())
    .filter(([, deg]) => deg === 0)
    .map(([id]) => id);

  const nodePosById = new Map<string, { x: number; y: number }>();
  for (const n of nodes) {
    nodePosById.set(n.id, n.position as { x: number; y: number });
  }
  const maxLevel = Math.max(0, ...Array.from(level.values()));

  const avgX = (ids: string[]) => {
    const xs = ids
      .map((id) => nodePosById.get(id)?.x)
      .filter((v): v is number => typeof v === "number");
    if (xs.length === 0) return 0;
    return xs.reduce((a, b) => a + b, 0) / xs.length;
  };

  const startX = avgX(entryIds);
  const endX = avgX(terminalIds);

  if (entryIds.length > 0) {
    nodes.push({
      id: "start",
      type: "circle",
      position: { x: startX, y: -LAYER_Y_GAP },
      data: { label: "Start", variant: "start" },
      draggable: false,
    });
    for (const id of entryIds) {
      edges.push({
        id: `start->${id}`,
        source: "start",
        target: id,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
      });
    }
  }

  if (terminalIds.length > 0) {
    nodes.push({
      id: "end",
      type: "circle",
      position: { x: endX, y: (maxLevel + 1) * LAYER_Y_GAP },
      data: { label: "End", variant: "end" },
      draggable: false,
    });
    for (const id of terminalIds) {
      edges.push({
        id: `${id}->end`,
        source: id,
        target: "end",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
      });
    }
  }

  return { nodes, edges };
}

function TutorialWorkflowNode(props: NodeProps<TutorialStepNode>) {
  const data = props.data;
  return (
    <div
      className={cn(
        "w-72 rounded-xl p-3 bg-linear-to-b from-[#f0f5ff] to-[#F8F9FC] border",
        data.isCurrent
          ? "border-[#535EA4] border-2 shadow-lg"
          : data.isCompleted
            ? "border-green-400 border-2"
            : "border-[#DDDDDD]",
      )}
    >
      <Handle type="target" position={Position.Top} />
      <div className="mb-1">
        <div className="data-icon w-10 h-10 mb-3 border-[1.5px] border-white flex items-center justify-center rounded-[5px]">
          <Cog className="h-5 w-5 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="flex-title-icon flex justify-between items-center gap-4 mb-2">
          <span
            className="truncate text-[#494949] text-[14px] font-semibold"
            title={data.name}
          >
            {data.name}
          </span>
          {data.isCompleted && (
            <CheckCircle2 className="ml-auto h-3.5 w-3.5 text-emerald-600 dark:text-emerald-300" />
          )}
        </div>
      </div>
      <div
        className="line-clamp-3 text-[#6B7280] text-xs"
        title={data.description}
      >
        {data.description}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

function CircleNode(props: NodeProps<CircleNodeType>) {
  const { data } = props;
  const isStart = data.variant === "start";
  return (
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 9999,
        background: "#fff",
        border: "1px solid #EAEAEA",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1F2937",
        fontSize: 12,
        fontWeight: 600,
      }}
      className="start-end points"
      title={data.label}
    >
      {data.label}
      {isStart ? <Handle type="source" position={Position.Bottom} /> : null}
      {!isStart ? <Handle type="target" position={Position.Top} /> : null}
    </div>
  );
}

interface TutorialWorkflowViewerProps {
  workflow: PlannerWorkflow | null | undefined;
  completedStepIds?: string[];
  currentStepId?: string;
  className?: string;
}

function TutorialWorkflowViewerInner({
  workflow,
  completedStepIds = [],
  currentStepId,
  className,
}: TutorialWorkflowViewerProps) {
  const { t } = useTranslation("docs");
  const { fitView } = useReactFlow();

  const { nodes, edges } = useMemo(() => {
    const graph = buildTutorialWorkflowGraph(
      workflow,
      completedStepIds,
      currentStepId,
    );
    return {
      ...graph,
      nodes: graph.nodes.map((node) =>
        node.id === "start" || node.id === "end"
          ? {
              ...node,
              data: {
                ...node.data,
                label: t(node.id === "start" ? "start" : "end"),
              },
            }
          : node,
      ),
    };
  }, [workflow, completedStepIds, currentStepId, t]);

  const [reactFlowNodes, setNodes, onNodesChange] = useNodesState(nodes);
  const [reactFlowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

  useEffect(() => {
    setNodes(nodes);
    setEdges(edges);
  }, [nodes, edges, setNodes, setEdges]);

  useEffect(() => {
    if (nodes.length > 0) {
      // Fit view after nodes/edges update and DOM paints
      requestAnimationFrame(() => {
        void fitView({
          padding: 0.25,
          includeHiddenNodes: false,
          duration: 250,
        });
      });
    }
  }, [nodes, edges, fitView]);

  const nodeTypes = useMemo(
    () =>
      ({
        tutorialWorkflow: TutorialWorkflowNode,
        circle: CircleNode,
        // `NodeTypes` is keyed on the untyped base `NodeProps`, so a map of
        // per-node-type components never matches it structurally. This is the
        // cast @xyflow's own examples use.
      }) as unknown as NodeTypes,
    [],
  );

  if (!workflow || nodes.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-64 bg-gray-50 rounded-lg dark:bg-zinc-800",
          className,
        )}
      >
        <p className="text-gray-500">{t("noWorkflow")}</p>
      </div>
    );
  }

  return (
    <div className={cn("h-full w-full", className)}>
      <ReactFlow
        nodes={reactFlowNodes}
        edges={reactFlowEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={false}
        edgesReconnectable={false}
        defaultEdgeOptions={{
          type: "default",
          style: { stroke: "#94a3b8", strokeWidth: 1.5 },
        }}
        panOnScroll
        zoomOnScroll
        panOnDrag
        fitView
        fitViewOptions={{ padding: 0.2, includeHiddenNodes: false }}
        minZoom={0.3}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <MiniMap
          nodeStrokeColor="#94a3b8"
          nodeColor="#e5e7eb"
          nodeBorderRadius={6}
          maskColor="rgba(0,0,0,0.08)"
        />
        <Controls showInteractive />
      </ReactFlow>
    </div>
  );
}

export function TutorialWorkflowViewer(props: TutorialWorkflowViewerProps) {
  return (
    <ReactFlowProvider>
      <div className="h-[500px] w-full border border-[#DDDDDD] rounded-lg overflow-hidden bg-white dark:bg-zinc-900">
        <TutorialWorkflowViewerInner {...props} />
      </div>
    </ReactFlowProvider>
  );
}
